import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getPathWithRef } from '../utils/attribution';
import { identifyPostHogUser } from '../posthog';
import { markApplicantSessionStarted } from '../utils/applicantSession';

const APPLICANT_ENTRY_INTENT_KEY = 'mvp_applicant_entry_intent';

/**
 * OAuth callback: ?token=JWT&intent=login|signup|complete_profile (intent optional; backend may pass it).
 * - Admin → /advisor.
 * - Signup flow (no intent or intent=signup): /signup/sso-details then contact, then user reaches /es/regularizacion/success.
 * - Login flow (intent=login) + non-admin: /organizer (organizer hub).
 * - Magic link flow (intent=complete_profile): /intake (landing-first).
 */
export default function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');
    const intent = searchParams.get('intent'); // 'login' | 'signup' | 'complete_profile' when provided by backend
    if (error) {
      setStatus('error');
      return;
    }
    if (!token) {
      navigate(getPathWithRef('/'), { replace: true });
      return;
    }
    localStorage.setItem('authToken', token);
    markApplicantSessionStarted();
    sessionStorage.setItem(
      APPLICANT_ENTRY_INTENT_KEY,
      intent === 'login' ? 'login' : (intent === 'complete_profile' ? 'complete_profile' : 'signup')
    );
    mvpApi.getMe()
      .then((res) => {
        const user = res.data?.user || res.data;
        if (user) localStorage.setItem('userData', JSON.stringify(user));
        // Optional: tie SPA activity to an identified user profile in PostHog.
        identifyPostHogUser(user);
        const role = user?.role?.value ?? user?.role;
        if (role === 'admin') {
          navigate('/advisor', { replace: true });
          return;
        }
        if (intent === 'login') {
          navigate('/organizer', { replace: true });
          return;
        }
        if (intent === 'complete_profile') {
          navigate('/intake', { replace: true });
          return;
        }
        try {
          sessionStorage.removeItem('mvp_sso_signup_step1');
        } catch {
          // ignore
        }
        navigate('/signup/sso-details', { replace: true });
      })
      .catch(() => {
        setStatus('error');
      });
  }, [navigate, searchParams]);

  if (status === 'error') {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#F0F5F9' }}>
        <p>{t('authCallback.error')}</p>
        <button
          type="button"
          onClick={() => navigate(getPathWithRef('/'))}
          style={{ marginTop: 16, padding: '10px 20px', cursor: 'pointer' }}
        >
          {t('authCallback.goHome')}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, textAlign: 'center', color: '#7A90A8' }}>
      {t('signingIn')}
    </div>
  );
}
